import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { get } from "utils/request";
import Loading from "components/atoms/Loading";
import { useRouter } from "components/hooks";
import { getMementosForMagnet, getUserMementosForMagnet } from "apis/magnets";
import { useUserContext } from "components/context/UserContext";
import MagnetPageMementoCard, { MagnetPageMementoCardDesktop } from "./MementoCard";
import { TOAST_CONFIG } from "../../constants";
import { MementoEmptyStates } from "./EmptyStates";

function MagnetMementos({
  isMagnetOwnedByUser,
  isMobile,
  isCustomizedUIBrand = false,
}: {
  isMagnetOwnedByUser?: boolean;
  isMobile?: boolean;
  isCustomizedUIBrand?: boolean;
}) {
  const { setCurrentlyViewingMementoId } = useUserContext();

  const router = useRouter();

  const { magnetId } = router.query;

  const handleGetAllMagnetMenentosError = (errorMessage: string) => {
    toast.error(errorMessage, TOAST_CONFIG);
  };

  const {
    isLoading,
    data: allMagnetMementoData,
    error,
    isFetching,
  } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ["memento", magnetId],
    queryFn: () =>
      get(!isMagnetOwnedByUser ? getMementosForMagnet(magnetId) : getUserMementosForMagnet(magnetId), {
        processError: handleGetAllMagnetMenentosError,
      }),
  });

  if (isLoading && isFetching) return <Loading />;

  if (error) return <p>Something went wrong</p>;

  const handleMementoClick = (mementoId: string) => {
    if (!isMagnetOwnedByUser) {
      setCurrentlyViewingMementoId(mementoId);
      return;
    }

    const mementoItem = allMagnetMementoData.find((mementos: Memento) => mementos.id === mementoId) as InventoryItem;
    setCurrentlyViewingMementoId(mementoItem.object_id);
  };

  return isMobile ? (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-4">
      {!allMagnetMementoData?.length ? (
        <div className="col-span-3">
          <MementoEmptyStates isDark={isCustomizedUIBrand} />
        </div>
      ) : (
        // TODO: Fix the logic here
        allMagnetMementoData
          .filter((memento: Memento) => !memento.is_incentive)
          .map((memento: Memento) => (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>
              {!["poll", "social_share"].includes(memento.activation_key) && (
                <MagnetPageMementoCard
                  key={`${memento.id}magnetPageMementoCard`}
                  actionPending={memento.action_pending}
                  name={memento.name}
                  imageUrl={memento.image_url || (memento?.thumbnail_url as string)}
                  mementoType={memento.activation_key}
                  isClickable
                  handleOnClick={() => handleMementoClick(memento.id)}
                />
              )}
            </>
          ))
      )}
    </div>
  ) : (
    <div className="secondary-scrollbar grid-flow-cols grid grid-cols-2 gap-y-4 overflow-y-auto px-4 pb-2 lg:grid-cols-3 lg:gap-y-7 2xl:px-8">
      {!allMagnetMementoData?.length ? (
        <div className="col-span-3">
          <MementoEmptyStates isDark={isCustomizedUIBrand} />
        </div>
      ) : (
        // TODO: Fix the logic here
        allMagnetMementoData
          .filter((memento: Memento) => !memento.is_incentive)
          .map((memento: Memento) => (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>
              {!["poll", "social_share"].includes(memento.activation_key) && (
                <MagnetPageMementoCardDesktop
                  textDark={isCustomizedUIBrand}
                  key={`${memento.id}brandPublicProfileMementoCard`}
                  actionPending={memento.action_pending}
                  name={memento.name}
                  imageUrl={memento.image_url || (memento?.thumbnail_url as string)}
                  mementoType={memento.activation_key}
                  isClickable
                  handleOnClick={() => handleMementoClick(memento.id)}
                />
              )}
            </>
          ))
      )}
    </div>
  );
}
export default MagnetMementos;
